<template>
  <div
    class="autocomplete"
    :class="[
      { 'autocomplete--has-value': hasValue },
      { 'autocomplete--disabled': disabled }
    ]"
  >
    <v-autocomplete
      v-if="multiple"
      v-model="model"
      :placeholder="placeholder"
      prepend-inner-icon="search"
      :search-input.sync="search"
      :items="entries"
      :chips="multiple"
      :multiple="multiple"
      :loading="isLoading"
      :disabled="disabled"
      :readonly="readonly"
      :autofocus="autofocus"
      :required="required"
      :rules="rules"
      :error="!isValid"
      color="accent"
      item-text="name"
      item-value="name"
      return-object
      hide-no-data
      hide-selected
      hide-details
      :noDataText="$t(`${namespace}.noDataText`)"
      outline
      solo
      flat
      @keyup="handleSearch"
      @input="emitValues"
      @blur="handleBlur"
      @click="requestOnClick && searchApi()"
    >
      <template slot="selection" slot-scope="data">
        <v-chip
          :data-costCenter-id="data.item.id"
          class="chip--select-multi"
          :title="data.item.name"
          close
          small
          @input="remove(data.item)"
        >
          {{ data.item.name | ellipsis(35) }}
        </v-chip>
      </template>
    </v-autocomplete>

    <v-autocomplete
      v-else
      v-model="model"
      :placeholder="placeholder"
      prepend-inner-icon="search"
      :search-input.sync="search"
      :items="entries"
      :loading="isLoading"
      :disabled="disabled"
      :readonly="readonly"
      :autofocus="autofocus"
      :required="required"
      :rules="rules"
      :error="!isValid"
      color="accent"
      item-text="name"
      item-value="name"
      :noDataText="$t(`${namespace}.noDataText`)"
      return-object
      hide-no-data
      hide-selected
      hide-details
      clearable
      outline
      solo
      flat
      @keyup="handleSearch"
      @input="emitValues"
      @blur="handleBlur"
      @click="requestOnClick && searchApi()"
    />
    <div v-if="hideDetails" class="autocomplete__messages">
      <span v-if="required && !isValid">{{ validateMessage || $t(`${namespace}.validationMessage`) }}</span>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import { isArray, isObject } from 'utils/functions'
import debounce from 'lodash.debounce'

export default {
  name: 'AutoComplete',
  props: {
    data: {
      type: [Array, Object],
      default: () => []
    },
    kind: {
      type: String
    },
    companyId: {
      type: [String, Number]
    },
    multiple: {
      type: Boolean,
      default: false
    },
    isValid: {
      type: Boolean,
      default: true
    },
    required: {
      type: Boolean,
      default: false
    },
    validateMessage: {
      type: String,
      default: ''
    },
    disabled: {
      type: Boolean,
      default: false
    },
    readonly: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: ''
    },
    employeeId: {
      type: [String, Number],
      default: null
    },
    hideDetails: {
      type: Boolean,
      default: true
    },
    requestOnClick: {
      type: Boolean,
      default: false
    },
    costCenterId: {
      type: [String, Number]
    },
    autofocus: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState({
      company: state => state.company.selectedCompany
    }),

    getCompanyId () {
      return this.companyId || this.company.id
    },

    hasValue () {
      return (Array.isArray(this.model) && this.model.length) ||
      (this.model && typeof this.model === 'object' && Object.keys(this.model).length)
    }
  },
  data () {
    return {
      namespace: 'Components.AutoComplete',
      model: null,
      search: null,
      entries: [],
      isLoading: false,
      rules: [],
      page: 1,
      limitPerPage: 25,
      firstLoad: true,
      preventEmit: false
    }
  },
  methods: {
    ...mapActions({
      errorAlert: 'snackbarAlert/error',
      infoAlert: 'snackbarAlert/info'
    }),

    isValidArray (val) {
      return isArray(val)
    },

    isValidObject (val) {
      return isObject(val)
    },

    validateProperties () {
      const kinds = ['costCenter', 'employee', 'account', 'project', 'webCallEmployee', 'webCallCostCenter', 'webCallCompany', 'company', 'owners', 'webCallProject', 'rideCredit', 'companyGroup']
      const checkKind = kinds.find(item => item === this.kind)

      if (!checkKind) {
        console.warn(`AutoComplete component error: The value for prop "kind" is not valid, use ${kinds.join(', ')}`)
        return
      }

      if (this.kind === 'webCallCostCenter' && !this.employeeId) {
        console.warn('AutoComplete component error: employeeId property is not valid.')
        return
      }

      if (this.multiple && this.data && !Array.isArray(this.data)) {
        console.warn('AutoComplete component error: The "data" property must be an array.')
      }
    },

    remove (item) {
      // update model
      let obj = this.model.find(x => x.name === item.name)
      let ind = this.model.indexOf(obj)
      if (ind >= 0) this.model.splice(ind, 1)

      // update cost centers -- duplicated code to avoid mutating prop directly
      obj = this.data.find(x => x.name === item.name)
      ind = this.data.indexOf(obj)
      if (ind >= 0) this.data.splice(ind, 1)
      this.$emit('input', this.model)
    },

    async getCostCenter () {
      this.isLoading = true

      try {
        const { data: res } = await this.$apiService.getCostCentersAll(
          { company: this.getCompanyId },
          { page: this.page },
          { limit: this.limitPerPage },
          { search: this.search }
        )
        this.entries = [ ...res.data ]
        if (!this.entries.length) this.infoAlert(this.$t(`${this.namespace}.getCostCenterEmpty`))
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.getCostCenterError`))
      }

      this.isLoading = false
    },

    async getEmployee () {
      this.isLoading = true

      try {
        const { data: res } = await this.$apiService.getEmployees(
          { company: this.getCompanyId },
          { page: this.page },
          { limit: this.limitPerPage },
          { search: this.search }
        )
        this.entries = [ ...res.data ]
        if (!this.entries.length) this.infoAlert(this.$t(`${this.namespace}.getEmployeeEmpty`))
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.getEmployeeError`))
      }

      this.isLoading = false
    },

    async getAccounts () {
      this.isLoading = true

      try {
        const { data: res } = await this.$apiService.users.get(
          { page: this.page - 1 },
          { size: this.limitPerPage },
          { companyIds: this.getCompanyId },
          { search: this.search }
        )

        if (res && !res.valid) {
          const error = this.isValidArray(res.reasons) ? res.reasons[0] : null
          this.errorAlert({
            type: 'error',
            message: error || this.$t(`${this.namespace}.getAccountError`)
          })
        } else {
          const users = res.content ? res.content : []

          this.entries = users
            .map(item => ({
              id: item.corporateId,
              name: item.name,
              email: item.email
            }))

          if (!this.entries.length) this.infoAlert(this.$t(`${this.namespace}.getAccountEmpty`))
        }
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.getAccountError`))
      }

      this.isLoading = false
    },

    async getProject () {
      this.isLoading = true

      try {
        const { data: res } = await this.$apiService.getProjects(
          { company: this.getCompanyId },
          { page: this.page },
          { limit: this.limitPerPage },
          { search: this.search }
        )
        this.entries = [ ...res.data ]
        if (!this.entries.length) this.infoAlert(this.$t(`${this.namespace}.getProjectEmpty`))
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.getProjectError`))
      }

      this.isLoading = false
    },

    async getWebCallEmployee () {
      this.isLoading = true

      try {
        const { data: res } = await this.$apiService.getWebCallEmployee(
          { companyID: this.getCompanyId },
          { page: this.page },
          { limit: this.limitPerPage },
          { search: this.search }
        )
        this.entries = [ ...res.data ]
        if (!this.entries.length) this.infoAlert(this.$t(`${this.namespace}.getWebCallEmployeeEmpty`))
      } catch (err) {
        console.log('Error', err)
        err.response && err.response.status === 404
          ? this.infoAlert(this.$t(`${this.namespace}.getWebCallEmployeeNotFound`))
          : this.errorAlert(this.$t(`${this.namespace}.getWebCallEmployeeError`))
      }

      this.isLoading = false
    },

    async getWebCallCostCenter () {
      this.isLoading = true

      try {
        const { data: res } = await this.$apiService.getWebCallCostCenter(
          this.employeeId,
          { company: this.getCompanyId },
          { page: this.page },
          { limit: this.limitPerPage },
          { search: this.search }
        )
        this.entries = [ ...res.data ]
        if (!this.entries.length) this.infoAlert(this.$t(`${this.namespace}.getWebCallCostCenterEmpty`))
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.getWebCallCostCenterError`))
      }

      this.isLoading = false
    },

    async getWebCallProject () {
      this.isLoading = true

      try {
        const { data: res } = await this.$apiService.getWebCallProjects(
          this.costCenterId,
          { page: this.page },
          { limit: this.limitPerPage },
          { search: this.search }
        )
        this.entries = [ ...res.data ]
        if (!this.entries.length) this.infoAlert(this.$t(`${this.namespace}.getProjectEmpty`))
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.getProjectError`))
      }

      this.isLoading = false
    },

    async getWebCallCompanies () {
      this.isLoading = true

      try {
        const { data: res } = await this.$apiService.getWebCallCompanies()

        // TO DO
        this.entries = [ ...res.data ].filter(company => company.status.toLowerCase() === 'enabled')
        if (!this.entries.length) this.infoAlert(this.$t(`${this.namespace}.getWebCallCompanyEmpty`))
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.getWebCallCompanyError`))
      }

      this.isLoading = false
    },

    async getCompany () {
      if (!this.search) return

      this.isLoading = true

      try {
        let search = this.search
        if (search.length === 18 && /\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}/.test(search)) {
          search = search.replace(/[-./]/g, '')
        }
        const { data: res } = await this.$apiService.searchCompany(search)
        this.entries = [ ...res.data ]
        if (!this.entries.length) this.infoAlert(this.$t(`${this.namespace}.getWebCallCompanyEmpty`))
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.getWebCallCompanyError`))
      }

      this.isLoading = false
    },

    async getOwners () {
      if (!this.search) return

      this.isLoading = true

      try {
        const { data: res } = await this.$apiService.users.get(
          { page: this.page - 1 },
          { size: this.limitPerPage },
          { profiles: 'OwnerUser' },
          { companyIds: this.getCompanyId },
          { search: this.search }
        )

        if (res && !res.valid) {
          const error = this.isValidArray(res.reasons) ? res.reasons[0] : null
          this.errorAlert({
            type: 'error',
            message: error || this.$t(`${this.namespace}.getAccountError`)
          })
        } else {
          const users = res.content ? res.content : []

          this.entries = users
            .map(item => ({
              id: item.corporateId,
              name: item.name,
              email: item.email
            }))

          if (!this.entries.length) this.infoAlert(this.$t(`${this.namespace}.getAccountEmpty`))
        }
      } catch (err) {
        console.log('Error', err)
        this.errorAlert(this.$t(`${this.namespace}.getAccountError`))
      }

      this.isLoading = false
    },

    async searchApiTaxiJobId () {
      if (!this.search) return

      this.isLoading = true

      try {
        const { data: res } = await this.$apiService.searchApiTaxiJobId(this.search)

        if (res && !res.valid) {
          const error = this.isValidArray(res.reasons) ? res.reasons[0] : res.message
          this.errorAlert({
            type: 'error',
            message: error || this.$t(`${this.namespace}.rideCreditError`)
          })
        } else {
          const arrRides = res.content ? [{ ...res.content }] : []

          this.entries = arrRides
            .filter(item => item.orderId)
            .map(item => ({
              id: item.id,
              name: item.detail.apiInfo.apiTaxiJobId,
              apiTaxiJobId: item.detail.apiInfo.apiTaxiJobId,
              ride: item
            }))

          if (!this.entries.length) this.infoAlert(this.$t(`${this.namespace}.rideCreditEmpty`))
        }
      } catch (err) {
        console.log('ERROR:', err)
        this.errorAlert(this.$t(`${this.namespace}.rideCreditError`))
      }

      this.isLoading = false
    },

    async getCompanyGroup () {
      if (!this.search) return

      this.isLoading = true

      try {
        const name = this.search
        const { data: res } = await this.$apiService.companyGroup.get(
          { page: 1 },
          { limit: 25 },
          { name }
        )

        if (!this.isValidArray(res.data)) {
          this.infoAlert(this.$t(`${this.namespace}.getCompanyGroupEmpty`))
        } else {
          this.entries = res.data.map(group => group.companyGroup)
        }
      } catch (err) {
        let msg = this.$t(`${this.namespace}.getCompanyGroupError`)

        if (err && err.response && err.response.data && this.isValidObject(err.response.data.errors)) {
          const errors = err.response.data.errors
          const key = Object.keys(errors)
          if (errors[key]) msg = errors[key][0]
        }

        this.errorAlert(msg)
      }

      this.isLoading = false
    },

    searchApi () {
      if (this.kind === 'costCenter') {
        this.getCostCenter()
        return
      }

      if (this.kind === 'employee') {
        this.getEmployee()
        return
      }

      if (this.kind === 'account') {
        this.getAccounts()
        return
      }

      if (this.kind === 'project') {
        this.getProject()
        return
      }

      if (this.kind === 'webCallEmployee') {
        this.getWebCallEmployee()
        return
      }

      if (this.kind === 'webCallCostCenter') {
        this.getWebCallCostCenter()
        return
      }

      if (this.kind === 'webCallProject') {
        this.getWebCallProject()
        return
      }

      if (this.kind === 'webCallCompany') {
        this.getWebCallCompanies()
        return
      }

      if (this.kind === 'company') {
        this.getCompany()
        return
      }

      if (this.kind === 'owners') {
        this.getOwners()
        return
      }

      if (this.kind === 'rideCredit') {
        this.searchApiTaxiJobId()
      }

      if (this.kind === 'companyGroup') {
        this.getCompanyGroup()
      }
    },

    handleSearch: debounce(function (val) {
      this.searchApi()
    }, 1000),

    setValue () {
      if (Array.isArray(this.data)) {
        const model = Array.isArray(this.model) ? this.model : []
        this.model = [ ...this.data ].concat(model)
        this.entries = [ ...this.data ].concat(model)
        return
      }

      if (this.data) {
        this.model = this.data
        this.entries = [{ ...this.data }]
      }
    },

    emitValues (val) {
      if (this.multiple) {
        this.search = null
        this.setValue()
      }

      this.model = val
      this.$emit('input', this.model)
    },

    handleBlur () {
      this.$emit('blur')

      if (!this.preventEmit) {
        this.$emit('input', this.model)
        this.preventEmit = true
        window.setTimeout(() => {
          this.preventEmit = false
        }, 1000)
      }
    }
  },
  watch: {
    search (val) {
      if (!this.multiple && !val) this.$emit('reset', null)
    },

    model (val) {
      if (this.firstLoad) this.firstLoad = false
    }
  },
  mounted () {
    this.validateProperties()
    this.setValue()
  }
}
</script>

<style lang="scss" scoped>
.autocomplete__messages {
  font-size: 10px !important;
}
</style>
